// PassFirst design tokens — single source of truth.
//
// See docs/pm-handoff/03-design-direction.md for the rationale and
// docs/design_refresh/README.md for the 2026 brand-refresh diffs.
// Brief in one line: calm, credible, capable. Navy primary, coral CTA,
// muted state colors that survive 70-hour study marathons. One accent
// across every vertical — per-credential identity is name + line icon,
// never a unique color.

// ---------------------------------------------------------------------------
// Raw palette — do not consume directly from app code. Reach for the
// semantic `colors` map below so renames don't ripple through screens.
// ---------------------------------------------------------------------------

export const palette = {
  // Brand
  navy: "#0F2A4A",
  navyHover: "#0A1F38",
  navySoft: "#E8EDF4",
  navyTint: "#F4F7FB",

  coral: "#FF6B47",
  coralHover: "#E55A38",
  coralSoft: "#FFF1ED",
  // WCAG AA: darker coral for CTA fills (white text on coral was 2.82:1).
  // coralDeep gives 5.29:1 white-on-fill in both themes.
  coralDeep: "#C43A1C",
  coralDeepHover: "#A8331A",
  // Deepened verdict-chip text (passes AA on the soft state grounds, light theme).
  greenDeep: "#0E7A41",
  amberDeep: "#8A5A0B",
  brickDeep: "#A23528",
  // Lightened verdict-chip text — reads on the dark soft grounds.
  greenLight: "#6FE3A6",
  amberLight: "#EBC976",
  brickLight: "#EC9A8B",

  // States — muted on purpose; bright versions trigger test-anxiety arousal.
  greenConfirm: "#16A85B",
  greenSoft: "#E5F5EC",
  amberCaution: "#D69A1F",
  amberSoft: "#FAF1DD",
  brickError: "#C04A3C",
  brickSoft: "#FBE9E6",

  // Neutrals — light theme. Brief: "no pure black on pure white."
  ink: "#15171C",
  paper: "#FAFAFB",
  white: "#FFFFFF",
  black: "#000000",

  // Grey ramp
  grey900: "#15171C",
  grey700: "#3D4148",
  grey500: "#6B7280",
  grey400: "#9AA0A9",
  grey300: "#C9CDD3",
  grey200: "#E2E5EA",
  grey100: "#F1F3F6",

  // Dark theme — wired up for use via `darkColors`. Long study sessions
  // make dark mode a comfort feature, mandatory from MVP.
  darkBackground: "#0E1015",
  darkSurface: "#161A21",
  darkSurfaceMuted: "#1E232C",
  darkBorder: "#2A313C",
  darkBorderSoft: "#232932",
  darkTextPrimary: "#E6E8ED",
  darkTextSecondary: "#B4BAC4",
  darkTextMuted: "#828B98",
  darkNavySoft: "#1C2A3D",
  darkCoralSoft: "#2A1B16",
  darkGreenSoft: "#15271E",
  darkAmberSoft: "#2A2412",
  darkBrickSoft: "#2A1714",
  // Dark-mode "navy flips" — navy disappears against the near-black ground,
  // so anything that is navy on light (headings, progress fill, selected-row
  // text, active-tab tint, badges, magic-link links) maps to these instead.
  // See docs/design_refresh/design_handoff_2 (README "Dark-mode gotcha").
  darkHeading: "#FFFFFF", // navy headings flip to white
  softNavyText: "#9FC0F0", // badges, active tab, links on dark
  skyProgress: "#4F86C9", // progress fill on dark (navy invisible)
  darkCardNavy: "#16243A", // readiness/brand card surface on dark
  darkCardNavyBorder: "#243349",
  darkVoiceNavy: "#0A121F", // immersive voice surface on dark
  darkNavyTint: "#141D2C", // Socratic tutor card surface on dark
} as const;

// ---------------------------------------------------------------------------
// Semantic colors — what app code should reference. The mapping layer means
// rebrands and dark-mode swaps land in one file, not scattered across screens.
// `colors` is the light theme; `darkColors` is the dark theme; a theme
// context (added later) picks between them at render time.
// ---------------------------------------------------------------------------

export const colors = {
  // Surfaces
  background: palette.paper,
  surface: palette.white,
  surfaceMuted: palette.grey100,
  surfacePicked: palette.navySoft,
  surfaceCorrect: palette.greenSoft,
  surfaceWrong: palette.brickSoft,
  surfaceCaution: palette.amberSoft,
  // Socratic tutor card ground; coral-soft action-tile icon ground.
  surfaceTutor: palette.navyTint,
  surfaceActionSoft: palette.coralSoft,

  // Text
  textPrimary: palette.ink,
  textSecondary: palette.grey700,
  textMuted: palette.grey500,
  textInverse: palette.white,
  textBrand: palette.navy,
  textCorrect: palette.greenConfirm,
  textWrong: palette.brickError,

  // Brand (titles, headers, brand surfaces)
  brand: palette.navy,
  brandHover: palette.navyHover,
  // Screen headings (H2/H3). Navy on light; flips to white on dark — kept
  // separate from `brand` so the flip lands without dragging chrome with it.
  heading: palette.navy,
  // "Soft-navy" accent text — free-tier badges, active tab tint, magic-link
  // links, selected-row text. Navy on light, lighter sky-navy on dark.
  accentSoftText: palette.navy,
  // Progress-track fill. Navy on light, sky-navy on dark (navy vanishes).
  progressFill: palette.navy,
  // Navy "brand card" surface (home readiness card). Navy on both, but a
  // touch lighter on dark so it reads as a raised card, not the ground.
  surfaceBrand: palette.navy,
  surfaceBrandBorder: palette.navy,
  // Immersive full-bleed navy surface (voice coach).
  surfaceImmersive: palette.navy,

  // Primary CTA — coral. Brief: "one orange element per screen at most."
  // Darkened to coralDeep for WCAG AA white-on-fill contrast (5.29:1).
  action: palette.coralDeep,
  actionHover: palette.coralDeepHover,
  actionText: palette.white,

  // Verdict-chip text — deepened for AA on the soft state grounds.
  verdictReady: palette.greenDeep,
  verdictAlmost: palette.amberDeep,
  verdictNotReady: palette.brickDeep,

  // Borders
  border: palette.grey300,
  borderSoft: palette.grey200,
  borderStrong: palette.grey500,
  borderBrand: palette.navy,
  borderPicked: palette.navy,
  borderCorrect: palette.greenConfirm,
  borderWrong: palette.brickError,

  // Status (use sparingly)
  success: palette.greenConfirm,
  caution: palette.amberCaution,
  error: palette.brickError,
} as const;

export const darkColors = {
  background: palette.darkBackground,
  surface: palette.darkSurface,
  surfaceMuted: palette.darkSurfaceMuted,
  surfacePicked: palette.darkNavySoft,
  surfaceCorrect: palette.darkGreenSoft,
  surfaceWrong: palette.darkBrickSoft,
  surfaceCaution: palette.darkAmberSoft,
  surfaceTutor: palette.darkNavyTint,
  surfaceActionSoft: palette.darkCoralSoft,

  textPrimary: palette.darkTextPrimary,
  textSecondary: palette.darkTextSecondary,
  textMuted: palette.darkTextMuted,
  textInverse: palette.darkBackground,
  textBrand: palette.darkTextPrimary,
  textCorrect: palette.greenConfirm,
  textWrong: palette.brickError,

  brand: palette.navy,
  brandHover: palette.navyHover,
  heading: palette.darkHeading,
  accentSoftText: palette.softNavyText,
  progressFill: palette.skyProgress,
  surfaceBrand: palette.darkCardNavy,
  surfaceBrandBorder: palette.darkCardNavyBorder,
  surfaceImmersive: palette.darkVoiceNavy,

  action: palette.coralDeep,
  actionHover: palette.coralDeepHover,
  actionText: palette.white,

  // Verdict-chip text — lighter tones read on the dark soft grounds.
  verdictReady: palette.greenLight,
  verdictAlmost: palette.amberLight,
  verdictNotReady: palette.brickLight,

  border: palette.darkBorder,
  borderSoft: palette.darkBorderSoft,
  borderStrong: palette.grey500,
  borderBrand: palette.darkTextPrimary,
  borderPicked: palette.coral,
  borderCorrect: palette.greenConfirm,
  borderWrong: palette.brickError,

  success: palette.greenConfirm,
  caution: palette.amberCaution,
  error: palette.brickError,
} as const;

export type SemanticColor = keyof typeof colors;

// ---------------------------------------------------------------------------
// Typography. Brief refresh (2026): off Inter onto Hanken Grotesk
// (UI + display) + Newsreader (serif, question stems ONLY) + JetBrains
// Mono (eyebrows, labels, code). Each weight is its own family name on
// React Native — there is no `fontWeight: "600"` shortcut, so reference
// `fontFamily.semiBold` directly. Fonts loaded via
// @expo-google-fonts/hanken-grotesk + @expo-google-fonts/newsreader in
// the client root.
// ---------------------------------------------------------------------------

export const fontFamily = {
  regular: "HankenGrotesk_400Regular",
  medium: "HankenGrotesk_500Medium",
  semiBold: "HankenGrotesk_600SemiBold",
  bold: "HankenGrotesk_700Bold",
  extraBold: "HankenGrotesk_800ExtraBold",
  // Serif — practice.tsx question stems + SEO inline-quiz stems ONLY.
  serif: "Newsreader_400Regular",
  serifMedium: "Newsreader_500Medium",
  // Mono — eyebrow labels, code, monospaced data.
  mono: "JetBrainsMono_400Regular",
  monoMedium: "JetBrainsMono_500Medium",
} as const;

export const fontSize = {
  micro: 11,
  caption: 13,
  ui: 15,
  body: 17,
  bodyLarge: 19,
  h3: 20,
  h2: 28,
  h1: 40,
  display: 64,
} as const;

// Preset text styles — pair size + weight + line height. Composing screens
// from these keeps the type scale to ~7 tokens; the brief caps at ~5 distinct
// sizes per screen, which falls out naturally if screens lean on these.
export const textStyle = {
  display: {
    fontFamily: fontFamily.extraBold,
    fontSize: fontSize.display,
    lineHeight: 64,
  },
  h1: { fontFamily: fontFamily.extraBold, fontSize: fontSize.h1, lineHeight: 44 },
  h2: { fontFamily: fontFamily.bold, fontSize: fontSize.h2, lineHeight: 31 },
  h3: {
    fontFamily: fontFamily.semiBold,
    fontSize: fontSize.h3,
    lineHeight: 25,
  },
  bodyLarge: {
    fontFamily: fontFamily.regular,
    fontSize: fontSize.bodyLarge,
    lineHeight: 28,
  },
  body: {
    fontFamily: fontFamily.regular,
    fontSize: fontSize.body,
    lineHeight: 26,
  },
  bodySmall: {
    fontFamily: fontFamily.regular,
    fontSize: fontSize.ui,
    lineHeight: 22,
  },
  caption: {
    fontFamily: fontFamily.regular,
    fontSize: fontSize.caption,
    lineHeight: 18,
  },
  // Practice question stems — serif by design, signaling "read this
  // carefully, it's content not chrome." Use ONLY for question.stem
  // rendering in practice.tsx and the SEO inline-quiz stem.
  questionStem: {
    fontFamily: fontFamily.serif,
    fontSize: 22,
    lineHeight: 32,
  },
  // Mono eyebrows — section headers like "FREE TIER · 5 OF 20".
  eyebrow: {
    fontFamily: fontFamily.monoMedium,
    fontSize: fontSize.caption,
    lineHeight: 16,
    letterSpacing: 1.4,
  },
  buttonPrimary: {
    fontFamily: fontFamily.semiBold,
    fontSize: fontSize.body,
    lineHeight: 20,
  },
  buttonSecondary: {
    fontFamily: fontFamily.semiBold,
    fontSize: fontSize.ui,
    lineHeight: 18,
  },
  label: {
    fontFamily: fontFamily.semiBold,
    fontSize: fontSize.ui,
    lineHeight: 18,
  },
} as const;

// ---------------------------------------------------------------------------
// Spacing — 8px base unit per the brief. The `space[N]` index reads "N
// times 4px," so `space[6]` = 24px, the brief's mobile content-block gap.
// ---------------------------------------------------------------------------

export const space = {
  0: 0,
  1: 4,
  2: 8,
  3: 12,
  4: 16,
  5: 20,
  6: 24,
  8: 32,
  10: 40,
  12: 48,
  16: 64,
  20: 80,
  24: 96,
  32: 128,
} as const;

export const radius = {
  sm: 6,
  md: 8,
  lg: 12,
  xl: 16,
  // The 2xl is for the marketing voice-coach card + onboarding hero tiles.
  xl2: 22,
  pill: 9999,
} as const;

// Navy-tinted, soft elevations — never harsh. RN's `shadow*` props approximate
// these on iOS; Android gets `elevation` and web maps to CSS box-shadow.
export const elevation = {
  sm: {
    shadowColor: palette.navy,
    shadowOpacity: 0.06,
    shadowRadius: 2,
    shadowOffset: { width: 0, height: 1 },
    elevation: 1,
  },
  md: {
    shadowColor: palette.navy,
    shadowOpacity: 0.08,
    shadowRadius: 16,
    shadowOffset: { width: 0, height: 4 },
    elevation: 3,
  },
  lg: {
    shadowColor: palette.navy,
    shadowOpacity: 0.14,
    shadowRadius: 48,
    shadowOffset: { width: 0, height: 18 },
    elevation: 8,
  },
} as const;

// ---------------------------------------------------------------------------
// Layout constants used in multiple screens.
// ---------------------------------------------------------------------------

export const layout = {
  // Brief: "design every screen for a 375px-wide canvas first; web gets to
  // be more spacious but doesn't get fundamentally different layouts."
  contentMaxWidth: 720,
  proseMaxWidth: 720,
  marketingMaxWidth: 1120,
  formMaxWidth: 360,
  // WCAG AA tap target.
  minTapSize: 44,
  // Web responsive constants (design_handoff_2):
  //   Rule A — practice/diagnostic switch focused→split at 900px (paywall
  //   stacks→row at 760). Rule B — bottom tab bar becomes a 236px sidebar.
  splitBreakpoint: 900,
  paywallBreakpoint: 760,
  sidebarWidth: 236,
  // Focused-column cap inside Rule A's <900 layout.
  focusedMaxWidth: 760,
} as const;
